<template>
  <div class="min-price">
    <div
      class="f fs gap4 all-prices__wrapper"
      @mouseenter="fetchPrices('from')"
    >
      <AllPricesButton
        :market="item.min_price?.market"
        :price="item.min_price?.price"
        @click="handleMainButtonClick(item.min_price?.market?.market_name || '', 'from')"
      />
      <LazyModalsComponentsComparisonMarketStats :opened="toggleFrom">
        <div class="all-prices__container">
          <div class="all-prices">
            <AllPricesHeader
              :average-price="data.average_price"
              :chart-market="chartFromMarket"
              :median-price="item?.min_price?.price"
              :rec-price="data.rec_price"
              :sales="item?.sales"
              :selected-market="selectedFromMarket"
              :show-img="chartFromMarket !== marketsFrom[0]?.market?.market_name"
              :title="t('price_details')"
              :updated-at="chartDataFrom?.updated_at"
            >
              <client-only>
                <LazyPagesComparisonItemComponentsCompareChart
                  v-if="chartDataFrom"
                  :is-initial="currW <= 470 || chartFromMarket === marketsFrom[0]?.market?.market_name"
                  :loading="chartLoading"
                  :values="chartDataFrom?.values || []"
                />
              </client-only>
            </AllPricesHeader>
            <AllPricesMarkets
              :active-markets="store.activeFilters.markets_from || []"
              :chart-market="chartFromMarket"
              :loading="loading"
              :markets="marketsFrom"
              way="from"
              @click="(marketName) => currW > 470 ? handleButtonClick(marketName) : fetchChart('from', marketName)"
              @mouseenter="(marketName) => fetchChart('from', marketName)"
            />
          </div>
        </div>
        <template #footer>
          <div class="all-prices__footer py8 px16">
            <button
              class="btn btn-primary fw500 btn-violet gap8 w-100"
              @click="handleButtonClick(chartFromMarket || '')"
            >
              <span>{{ t("visit_to_market") }}</span>
              <svg
                height="10"
                width="10"
              >
                <use xlink:href="/icons/sprite.svg#i-arrow-right-top" />
              </svg>
            </button>
          </div>
        </template>
      </LazyModalsComponentsComparisonMarketStats>
    </div>
  </div>

  <svg
    class="gray"
    height="20"
    width="20"
  >
    <use xlink:href="/icons/sprite.svg#i-arrow-right-circle" />
  </svg>

  <div class="max-price f fs gap8">
    <div
      class="f fs gap4 all-prices__wrapper"
      @mouseenter="fetchPrices('to')"
    >
      <AllPricesButton
        :market="item.max_price?.market"
        :price="item.max_price?.price"
        @click="handleMainButtonClick(item.max_price?.market?.market_name || '', 'to')"
      />
      <LazyModalsComponentsComparisonMarketStats :opened="toggleTo">
        <div class="all-prices__container">
          <div class="all-prices">
            <AllPricesHeader
              :average-price="data.average_price"
              :chart-market="chartToMarket"
              :median-price="item?.min_price?.price"
              :rec-price="data.rec_price"
              :sales="item?.sales"
              :selected-market="selectedToMarket"
              :show-img="chartFromMarket !== marketsFrom[0]?.market?.market_name"
              :title="t('price_details')"
              :updated-at="chartDataTo?.updated_at"
            >
              <client-only>
                <LazyPagesComparisonItemComponentsCompareChart
                  v-if="chartDataTo"
                  :is-initial="currW <= 470 || chartToMarket === marketsTo[0]?.market?.market_name"
                  :loading="chartLoading"
                  :values="chartDataTo?.values || []"
                />
              </client-only>
            </AllPricesHeader>
            <AllPricesMarkets
              :active-markets="store.activeFilters.markets_to || []"
              :chart-market="chartToMarket"
              :loading="loading"
              :markets="marketsTo"
              way="to"
              @click="(marketName) => currW > 470 ? handleButtonClick(marketName) : fetchChart('from', marketName)"
              @mouseenter="(marketName) => fetchChart('to', marketName)"
            />
          </div>
        </div>
        <template #footer>
          <div class="all-prices__footer py8 px16">
            <button
              class="btn btn-primary fw500 btn-violet gap8 w-100"
              @click="handleButtonClick(chartToMarket || '')"
            >
              <span>{{ t("visit_to_market") }}</span>
              <svg
                height="10"
                width="10"
              >
                <use xlink:href="/icons/sprite.svg#i-arrow-right-top" />
              </svg>
            </button>
          </div>
        </template>
      </LazyModalsComponentsComparisonMarketStats>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  CompareItem,
  ComparisonItemMinListingsResult,
  ComparisonMarketInfo,
  ComparisonMarketsInput,
  ComparisonPriceInfo,
} from '~/graphql/generated/schema-types';
import { encode } from 'urlencode';
import { EventBus } from '~/composables/bus';
import { ComparisonItemMinListingsPeriod, SalesPeriod } from '~/graphql/generated/schema-types';
import { useStore } from '~/store';
import { useComparisonStore } from '~/store/comparison';
import { useModalStore } from '~/store/modal';
import { ModalIds } from '~/types/Modals';
import { getComparisonMarketListings } from '~/utils/api';
import AllPricesButton from './AllPricesButton.vue';
import AllPricesHeader from './AllPricesHeader.vue';
import AllPricesMarkets from './AllPricesMarkets.vue';

const props = defineProps<{
  item: CompareItem
}>();

const currW = useResize();
const { activeModals, openModal } = useModalStore();

const { t } = useI18n();
const store = useComparisonStore();
const globalStore = useStore();
const rate = computed(() => globalStore.rate);

const config = useRuntimeConfig();
const baseUrlV2 = config.public.baseUrlV2;
const data = ref<ComparisonMarketInfo>({} as ComparisonMarketInfo);
const loading = ref(false);
const marketsFrom = ref<ComparisonPriceInfo[]>([]);
const marketsTo = ref<ComparisonPriceInfo[]>([]);

const chartDataFrom = ref<ComparisonItemMinListingsResult | null>(null);
const chartDataTo = ref<ComparisonItemMinListingsResult | null>(null);
const chartLoading = ref(false);
const chartFromMarket = ref('');
const chartToMarket = ref('');
const toggleFrom = ref(false);
const toggleTo = ref(false);

async function fetchChart(way: 'from' | 'to', name?: string) {
  if (chartLoading.value || !data.value?.offers?.length) {
    return;
  }
  await nextTick();

  try {
    chartLoading.value = true;
    const market
        = name || (way === 'from' ? marketsFrom : marketsTo).value[0]?.market?.market_name || '';

    (way === 'from' ? chartFromMarket : chartToMarket).value = market;

    const { data: chart, errors } = await getComparisonMarketListings({
      period:
          store.activeFilters.sales_period === SalesPeriod.Monthly
            ? ComparisonItemMinListingsPeriod.OneMonth
            : ComparisonItemMinListingsPeriod.SevenDays,
      market_names: [market],
      market_hash_name: props.item?.market_hash_name || '',
    });

    if (errors?.length) {
      throw new Error(errors[0].message);
    }
    (way === 'from' ? chartDataFrom : chartDataTo).value
        = chart?.comparisonItemMinListings || ({} as ComparisonItemMinListingsResult);
  } catch (e: any) {
    EventBus.$emit('notify', {
      success: false,
      msg: e.message || t('something_went_wrong'),
    });
  } finally {
    chartLoading.value = false;
  }
}

async function fetchPrices(way: 'from' | 'to') {
  if (Object.keys(data.value)?.length || loading.value) {
    await fetchChart(way);
    return;
  }
  loading.value = true;
  try {
    const filters: ComparisonMarketsInput = {
      link: props.item?.link || '',
      markets_from: store.activeFilters.markets_from || [],
      markets_to: store.activeFilters.markets_to || [],
      sales_period: store.activeFilters.sales_period,
    };

    if (store.activeFilters.price_from) {
      filters.price_from = store.activeFilters.price_from;
    }
    if (store.activeFilters.price_to) {
      filters.price_to = store.activeFilters.price_to;
    }
    if (store.activeFilters.offers) {
      filters.offers = store.activeFilters.offers;
    }

    const { data: newData, errors } = await getComparisonMarketsInfo(filters, rate.value);

    if (errors?.length) {
      throw new Error(errors[0].message);
    }

    data.value = newData?.compareMarketsInfo || {};
    marketsFrom.value = [];
    marketsTo.value = [];
    const notIncludedFrom = [] as ComparisonPriceInfo[];
    const notIncludedTo = [] as ComparisonPriceInfo[];

    newData?.compareMarketsInfo?.offers.forEach((item) => {
      if (store.activeFilters.markets_from?.includes(item?.market?.market_name || '')) {
        marketsFrom.value.push(item!);
        if (!store.activeFilters.markets_to?.includes(item?.market?.market_name || '')) {
          notIncludedTo.push(item!);
        }
      }
      if (store.activeFilters.markets_to?.includes(item?.market?.market_name || '')) {
        marketsTo.value.push(item!);
        if (!store.activeFilters.markets_from?.includes(item?.market?.market_name || '')) {
          notIncludedFrom.push(item!);
        }
      }
    });

    marketsFrom.value = [...marketsFrom.value, ...notIncludedFrom];
    marketsTo.value = [...marketsTo.value.reverse(), ...notIncludedTo.reverse()];
  } catch (e: any) {
    console.error(e);
    EventBus.$emit('notify', {
      success: false,
      msg: e.message || 'Something went wrong',
    });
  } finally {
    loading.value = false;
    await fetchChart(way);
  }
}

const { gtag } = useGtag();

function sendGtag(market: string) {
  gtag('event', 'csmarket-click', {
    event_category: 'offers',
    value: market,
  });
}

function getMarketLink(marketName: string) {
  return `${baseUrlV2}/redirect-to-market?marketName=${encode(marketName)}&itemName=${encodeURIComponent(props.item.market_hash_name)}&gameId=730`;
}

function handleButtonClick(marketName: string) {
  sendGtag(marketName);
  const marketLink = getMarketLink(marketName);
  window.open(marketLink, '_blank');
}

function handleMainButtonClick(marketName: string, way: 'from' | 'to') {
  if (currW.value > 470) {
    handleButtonClick(marketName);
    return;
  }

  (way === 'from' ? toggleFrom : toggleTo).value = true;
  openModal(ModalIds.COMPARISON_MARKETS_STATS);
  fetchPrices(way);
}

const selectedFromMarket = computed(() => {
  return marketsFrom.value.find((item) => {
    return item?.market?.market_name === chartFromMarket.value;
  });
});
const selectedToMarket = computed(() => {
  return marketsTo.value.find((item) => {
    return item?.market?.market_name === chartToMarket.value;
  });
});

watch(activeModals, (val) => {
  if (currW.value <= 470 && !val.has(ModalIds.COMPARISON_MARKETS_STATS)) {
    toggleFrom.value = false;
    toggleTo.value = false;
  }
});
</script>

<style lang="scss" scoped>
.all-prices {
  border-radius: 8px;
  border: 1px solid var(--surfaces-outline);
  background: var(--surfaces-container);
  padding: 16px;
  display: none;
  min-width: 150px;

  &__container {
    position: absolute;
    top: calc(100%);
    left: 50%;
    z-index: 1;
    transform: translateX(-50%);
    padding-top: 16px;
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    border: 8px solid transparent;
    border-bottom-color: var(--surfaces-outline);
  }

  .btn-primary {
    background: var(--surfaces-high-container);
  }

  &__wrapper {
    position: relative;
    width: 100%;
    @media screen and (min-width: 1114px) {
      &:hover .all-prices {
        display: block;
      }
    }
  }

  @media screen and (max-width: 470px) {
    display: block;
    border: none;
    width: 100%;

    &__container {
      position: static;
      top: unset;
      left: unset;
      transform: unset;
      padding-top: 0;
    }
    &:before {
      content: none;
    }
  }
}
</style>
